import React, { useState, useEffect, useRef } from 'react';
import { Upload, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import StargateTunnel from './StargateTunnel';

const UploadProgress = ({ progress, filename, totalSize = 0, uploadedBytes = 0, fileCount = 1, onCancel }) => {
  const [speed, setSpeed] = useState(0);
  const prevBytesRef = useRef(0);
  const prevTimeRef = useRef(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = (now - prevTimeRef.current) / 1000;
      if (elapsed > 0) {
        const bytesDiff = uploadedBytes - prevBytesRef.current;
        const currentSpeed = bytesDiff / elapsed;
        setSpeed(prev => prev * 0.3 + currentSpeed * 0.7);
      }
      prevBytesRef.current = uploadedBytes;
      prevTimeRef.current = now;
    }, 1000);
    return () => clearInterval(interval);
  }, [uploadedBytes]);

  const formatSize = (bytes) => {
    if (bytes <= 0) return '0 o';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
  };

  const formatSpeed = (bytesPerSec) => {
    if (bytesPerSec <= 0) return '-- /s';
    if (bytesPerSec < 1024 * 1024) return `${(bytesPerSec / 1024).toFixed(0)} Ko/s`;
    return `${(bytesPerSec / (1024 * 1024)).toFixed(1)} Mo/s`;
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0 || !isFinite(seconds) || seconds > 86400 * 7) return '--';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const secs = Math.round(seconds % 60);
      return `${minutes}m ${secs}s`;
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const remaining = totalSize - uploadedBytes;
  // Require a minimum sustained speed (1 KB/s) to display ETA — avoids absurd numbers at startup
  const eta = speed > 1024 ? remaining / speed : 0;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black" data-testid="upload-progress-modal">
      <StargateTunnel />
      
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, transparent 20%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.7) 100%)' }}
      />

      <div className="relative flex items-center justify-center min-h-screen p-6 z-10">
        <div className="bg-slate-950/80 backdrop-blur-2xl border-2 border-blue-400/50 rounded-3xl p-10 max-w-3xl w-full shadow-2xl shadow-blue-500/40 relative overflow-hidden">
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-500 opacity-20 blur-2xl animate-pulse"></div>
          
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 via-cyan-400 to-blue-500 rounded-full flex items-center justify-center relative shadow-lg shadow-blue-500/50">
                  <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-75"></div>
                  <Upload className="w-8 h-8 text-white relative z-10" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-2xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]">Transfert en cours</h3>
                  <p className="text-cyan-200 text-sm mt-1 truncate max-w-md drop-shadow-lg">
                    {fileCount > 1 ? `${fileCount} fichiers` : filename}
                  </p>
                </div>
              </div>
            </div>

            {/* Size info */}
            <div className="flex items-center justify-between mb-4 text-sm">
              <span className="text-blue-300/70">
                {formatSize(uploadedBytes)} / {formatSize(totalSize)}
              </span>
              <span className="text-blue-300/70" data-testid="remaining-size">
                Restant : {formatSize(remaining)}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="space-y-4">
              <div className="relative">
                <div className="h-5 bg-slate-800/60 rounded-full overflow-hidden backdrop-blur-sm border border-blue-500/20">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-500 transition-all duration-300 rounded-full relative overflow-hidden"
                    style={{ width: `${progress}%` }}
                    data-testid="progress-bar"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
                  </div>
                </div>
                <div className="absolute inset-0 h-5 rounded-full bg-blue-500/40 blur-lg"></div>
              </div>
              
              <div className="flex items-center justify-between text-lg">
                <span className="text-cyan-200 font-medium drop-shadow-lg">Progression</span>
                <span className="text-white font-bold text-2xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)] tabular-nums" data-testid="progress-percent">
                  {Math.round(progress)}%
                </span>
              </div>

              {/* Upload Stats Grid */}
              <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-blue-500/20">
                <div className="text-center">
                  <div className="text-blue-300/70 text-sm mb-1">Vitesse</div>
                  <div className="text-cyan-400 font-bold text-lg tabular-nums" data-testid="upload-speed">
                    {formatSpeed(speed)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-blue-300/70 text-sm mb-1">Taille totale</div>
                  <div className="text-cyan-400 font-bold text-lg tabular-nums" data-testid="total-size">
                    {formatSize(totalSize)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-blue-300/70 text-sm mb-1">Temps restant</div>
                  <div className="text-cyan-400 font-bold text-lg tabular-nums" data-testid="time-remaining">
                    {progress < 100 ? formatTime(eta) : '0s'}
                  </div>
                </div>
              </div>
            </div>

            {progress < 100 && onCancel && (
              <div className="mt-8 text-center">
                <Button
                  onClick={onCancel}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-5 rounded-xl text-base"
                  data-testid="cancel-upload-btn"
                >
                  <X className="w-5 h-5 mr-2" />
                  Annuler le transfert
                </Button>
              </div>
            )}

            {progress === 100 && (
              <div className="mt-6 text-center">
                <p className="text-emerald-300 text-lg font-semibold drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]">
                  Transfert terminé
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadProgress;
